/* model.js — pure client-side aggregation over the live snapshot.
   db = { invoices, expenses, customers, settings } where invoices are the raw
   invoice objects (with .lines and .payments) loaded from domain endpoints. */
(function () {
  const TODAY = new Date().toISOString().slice(0, 10);
  const iso = (v) => String(v).slice(0, 10);
  const dateUtc = (v) => { const [y, m, d] = iso(v).split("-").map(Number); return Date.UTC(y, m - 1, d); };
  const parseIso = (v) => { const [y, m, d] = iso(v).split("-").map(Number); return new Date(Date.UTC(y, m - 1, d)); };
  const toIso = (date) => date.toISOString().slice(0, 10);
  const daysBetween = (a, b) => Math.round((dateUtc(b) - dateUtc(a)) / 86400000);
  const monthIndex = (v) => Number(iso(v).slice(5, 7)) - 1;
  const yearOf = (v) => Number(iso(v).slice(0, 4));
  const daysInMonth = (year, zeroBasedMonth) => new Date(Date.UTC(year, zeroBasedMonth + 1, 0)).getUTCDate();

  // Treat anything that isn't an explicit Draft / Cancelled as a posted invoice.
  const isDraft = (iv) => /draft/i.test(iv.status || "");
  const isCancelled = (iv) => /cancel/i.test(iv.status || "");

  function totals(invoice) {
    const lines = invoice.lines || [];
    const subtotal = lines.reduce((s, l) => s + l.quantity * l.unitPrice, 0);
    const tax = lines.reduce((s, l) => s + l.quantity * l.unitPrice * l.taxRate, 0);
    const total = subtotal + tax;
    const paid = (invoice.payments || []).reduce((s, p) => s + p.amount, 0);
    return { subtotal, tax, total, paid, outstanding: total - paid };
  }

  function enrich(db, invoice) {
    const t = totals(invoice);
    const open = !isDraft(invoice) && !isCancelled(invoice) && t.outstanding > 0;
    const overdue = open && daysBetween(invoice.dueDate, TODAY) > 0;
    const partial = open && t.paid > 0;
    let display = "Open";
    if (isDraft(invoice)) display = "Draft";
    else if (isCancelled(invoice)) display = "Cancelled";
    else if (t.outstanding <= 0) display = "Paid";
    else if (overdue) display = "Overdue";
    else if (partial) display = "Partial";
    const customer = db.customers.find((c) => c.id === invoice.customerId) || null;
    return { invoice, totals: t, open, overdue, partial, display, customer, overdueDays: daysBetween(invoice.dueDate, TODAY) };
  }

  function expenseInMonth(x, year, m) {
    const start = dateUtc(x.date);
    const monthEnd = Date.UTC(year, m + 1, 0);
    const monthStart = Date.UTC(year, m, 1);
    const freq = x.recurrenceFrequency || "None";
    if (freq === "None") return (start >= monthStart && start <= monthEnd) ? 1 : 0;
    if (start > monthEnd) return 0;
    if (x.recurrenceEndDate && dateUtc(x.recurrenceEndDate) < monthStart) return 0;
    if (freq === "Monthly") return 1;
    if (freq === "Yearly") return monthIndex(x.date) === m ? 1 : 0;
    if (freq === "Weekly") { // approx: ~4.33/mo from start month onward
      return start <= monthEnd ? 4 : 0;
    }
    return 0;
  }

  function addExpenseOccurrence(date, freq) {
    if (freq === "Weekly") return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + 7));
    if (freq === "Yearly") return new Date(Date.UTC(date.getUTCFullYear() + 1, date.getUTCMonth(), Math.min(date.getUTCDate(), daysInMonth(date.getUTCFullYear() + 1, date.getUTCMonth()))));
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, Math.min(date.getUTCDate(), daysInMonth(date.getUTCFullYear(), date.getUTCMonth() + 1))));
  }

  function expenseOccurrences(expense, options = {}) {
    if (!expense || !expense.date) return [];
    const freq = expense.recurrenceFrequency || "None";
    const startIso = iso(expense.date);
    if (freq === "None") return options.includeFuture || dateUtc(startIso) <= dateUtc(options.until || TODAY) ? [startIso] : [];

    const today = parseIso(options.until || TODAY);
    const start = parseIso(expense.date);
    const configuredEnd = expense.recurrenceEndDate ? parseIso(expense.recurrenceEndDate) : today;
    const end = options.includeFuture || configuredEnd < today ? configuredEnd : today;
    const max = options.max || 120;
    const dates = [];
    for (let current = start, guard = 0; current <= end && guard < 600; guard++) {
      dates.push(toIso(current));
      current = addExpenseOccurrence(current, freq);
    }

    return max && dates.length > max ? dates.slice(-max) : dates;
  }

  function expensePaymentRows(db, options = {}) {
    return (db.expenses || []).flatMap((expense) => expenseOccurrences(expense, options).map((occurrenceDate) => {
      const payment = (expense.payments || []).find((item) => iso(item.occurrenceDate) === occurrenceDate) || null;
      return {
        expense,
        occurrenceDate,
        payment,
        amount: (expense.amount || 0) + (expense.taxAmount || 0),
        paid: !!payment,
      };
    })).sort((a, b) => b.occurrenceDate.localeCompare(a.occurrenceDate) || a.expense.category.localeCompare(b.expense.category));
  }

  function employeeMonthlyWage(employee) {
    const period = currentWagePeriod(employee);
    if (!period) return 0;
    if (period.wageFrequency === "Monthly") return period.wageAmount || 0;
    if (period.wageFrequency === "Annual") return (period.wageAmount || 0) / 12;
    if (period.wageFrequency === "Weekly") return (period.wageAmount || 0) * 4.33;
    if (period.wageFrequency === "Hourly") return (period.wageAmount || 0) * 160;
    return 0;
  }

  function currentWagePeriod(employee, onDate = TODAY) {
    const periods = (employee && employee.wagePeriods && employee.wagePeriods.length ? employee.wagePeriods : employee ? [{ startsOn: employee.startedOn || TODAY, wageAmount: employee.wageAmount || 0, wageFrequency: employee.wageFrequency || "Monthly" }] : [])
      .slice()
      .sort((a, b) => iso(a.startsOn || TODAY).localeCompare(iso(b.startsOn || TODAY)));
    return periods.find((period) => iso(period.startsOn || TODAY) <= onDate && (!period.endsOn || iso(period.endsOn) >= onDate)) || periods.at(-1) || null;
  }

  function wageExpenseAmount(period) {
    if (!period) return 0;
    if (period.wageFrequency === "Annual") return (period.wageAmount || 0) / 12;
    if (period.wageFrequency === "Hourly") return (period.wageAmount || 0) * 160;
    return period.wageAmount || 0;
  }

  function wageExpenseRecurrence(period) {
    return period && period.wageFrequency === "Weekly" ? "Weekly" : "Monthly";
  }

  function monthlyPnL(db, year) {
    const nowMonthStart = dateUtc(TODAY.slice(0, 7) + "-01");
    return Array.from({ length: 12 }, (_, m) => {
      const name = new Intl.DateTimeFormat("en", { month: "short" }).format(new Date(year, m, 1));
      let income = 0, billed = 0, expense = 0;
      db.invoices.forEach((iv) => {
        if (yearOf(iv.issueDate) === year && monthIndex(iv.issueDate) === m && !isDraft(iv) && !isCancelled(iv)) billed += totals(iv).total;
        (iv.payments || []).forEach((p) => { if (yearOf(p.paidOn) === year && monthIndex(p.paidOn) === m) income += p.amount; });
      });
      db.expenses.forEach((x) => { const o = expenseInMonth(x, year, m); if (o) expense += (x.amount + (x.taxAmount || 0)) * o; });
      return { name, month: m, income, billed, expense, net: income - expense, future: Date.UTC(year, m, 1) > nowMonthStart };
    });
  }

  function receivables(db) {
    const buckets = {
      current: { label: "Current", short: "Current", amount: 0, items: [], color: "#2856c4" },
      b30: { label: "1–30 days", short: "1–30d", amount: 0, items: [], color: "#0e7490" },
      b60: { label: "31–60 days", short: "31–60d", amount: 0, items: [], color: "#b45309" },
      b90: { label: "61+ days", short: "61d+", amount: 0, items: [], color: "#c0392b" },
    };
    db.invoices.forEach((iv) => {
      const e = enrich(db, iv);
      if (!e.open) return;
      const d = e.overdueDays;
      const key = d <= 0 ? "current" : d <= 30 ? "b30" : d <= 60 ? "b60" : "b90";
      buckets[key].amount += e.totals.outstanding;
      buckets[key].items.push(e);
    });
    const total = Object.values(buckets).reduce((s, b) => s + b.amount, 0);
    return { buckets, total };
  }

  function ledger(db, year) {
    const moves = [];
    db.invoices.forEach((iv) => {
      const cust = db.customers.find((c) => c.id === iv.customerId);
      (iv.payments || []).forEach((p) => {
        if (yearOf(p.paidOn) !== year) return;
        moves.push({ date: iso(p.paidOn), type: "in", category: "Payment", party: cust ? cust.name : "—", ref: iv.number, note: p.reference || "Payment received", amount: p.amount });
      });
    });
    db.expenses.forEach((x) => {
      for (let m = 0; m < 12; m++) {
        const o = expenseInMonth(x, year, m);
        if (!o) continue;
        const date = `${year}-${String(m + 1).padStart(2, "0")}-${iso(x.date).slice(8, 10)}`;
        if (dateUtc(date) > dateUtc(TODAY)) continue;
        moves.push({ date, type: "out", category: x.category, party: x.vendor, ref: x.isWage ? "WAGE" : ((x.recurrenceFrequency && x.recurrenceFrequency !== "None") ? "REC" : "EXP"), note: x.description, amount: -((x.amount + (x.taxAmount || 0)) * o), wage: x.isWage });
      }
    });
    moves.sort((a, b) => a.date.localeCompare(b.date) || (a.type === "in" ? -1 : 1));
    const net = moves.reduce((s, mv) => s + mv.amount, 0);
    const opening = (db.settings.currentCashBalance || 0) - net;
    let bal = opening;
    moves.forEach((mv) => { bal += mv.amount; mv.balance = bal; });
    return { moves, opening };
  }

  function cashflow(db) {
    let outstanding = 0, overdue = 0;
    db.invoices.forEach((iv) => {
      const e = enrich(db, iv);
      if (!e.open) return;
      outstanding += e.totals.outstanding;
      if (e.overdueDays > 0) overdue += e.totals.outstanding;
    });
    const m = monthIndex(TODAY), y = yearOf(TODAY);
    const p = monthlyPnL(db, y)[m];
    return { cashBalance: db.settings.currentCashBalance || 0, outstanding, overdue, netThisMonth: p.net, incomeThisMonth: p.income, expenseThisMonth: p.expense };
  }

  function projectWorkPackages(project) {
    return window.AC && window.AC.normalizeProjectWorkPackages
      ? window.AC.normalizeProjectWorkPackages(project.workPackages, project.planningNotes, project)
      : (project.workPackages || []);
  }

  function projectTickets(project) {
    return projectWorkPackages(project).flatMap((section) => (
      (section.ticketClusters || []).flatMap((cluster) => (
        (cluster.tickets || []).map((ticket) => ({ ...ticket, section, cluster, projectId: project.id }))
      ))
    ));
  }

  function requirementStatusLabel(status) {
    const value = String(status || "").trim().toLowerCase();
    if (value === "accepted" || value === "implemented" || value === "signed off") return "Signed off";
    if (value === "in review" || value === "review") return "In review";
    if (value === "open question" || value === "question") return "Open question";
    return "Draft";
  }

  function requirementStatusValue(status) {
    const value = String(status || "").trim().toLowerCase();
    if (value === "implemented") return "implemented";
    if (value === "accepted" || value === "signed off") return "accepted";
    if (value === "open question" || value === "question") return "open question";
    return "draft";
  }

  function projectKnowledgeConcepts(project) {
    const workPackagesById = new Map(projectWorkPackages(project).map((section) => [section.id, section]));
    const concepts = Array.isArray(project.knowledgeConcepts) ? project.knowledgeConcepts : [];
    return concepts
      .map((concept, index) => {
        const section = workPackagesById.get(concept.workPackageId) || null;
        const metadata = concept.metadata || {};
        const status = requirementStatusValue(concept.status || metadata.status);
        return {
          id: concept.id,
          position: Number(metadata.position) || index,
          title: concept.title || "",
          description: concept.description || "",
          path: concept.path || "",
          text: concept.bodyMarkdown || "",
          bodyMarkdown: concept.bodyMarkdown || "",
          conceptType: concept.type || "Requirement",
          tags: Array.isArray(concept.tags) ? concept.tags : [],
          metadata,
          type: metadata.requirementType || "Functional",
          priority: concept.priority || "Medium",
          status,
          statusLabel: requirementStatusLabel(concept.status || metadata.status),
          workPackageId: concept.workPackageId || "",
          sectionId: concept.workPackageId || "",
          sectionName: section ? section.name : "",
          createdAt: concept.createdAt || "",
          updatedAt: concept.updatedAt || "",
          covered: status === "accepted" || status === "implemented",
        };
      });
  }

  function projectRequirements(project) {
    const milestones = project.milestones || [];
    const tickets = projectTickets(project);
    const ticketsByRequirement = tickets.reduce((map, ticket) => {
      if (ticket.requirementId) map.set(ticket.requirementId, [...(map.get(ticket.requirementId) || []), ticket]);
      return map;
    }, new Map());
    return projectKnowledgeConcepts(project)
      .filter((concept) => String(concept.conceptType || "").toLowerCase() === "requirement")
      .map((concept) => {
        const linkedTickets = ticketsByRequirement.get(concept.id) || [];
        const sectionHasPlan = linkedTickets.length > 0 || milestones.some((milestone) => milestone.workPackageId === concept.workPackageId);
        return { ...concept, covered: concept.covered || sectionHasPlan };
      });
  }

  function milestoneDone(milestone) {
    return !!(milestone.completed || milestone.done || /done|completed/i.test(milestone.status || ""));
  }

  function projectReadiness(project) {
    const reqs = projectRequirements(project);
    const total = reqs.length;
    const signedOff = reqs.filter((req) => req.status === "accepted" || req.status === "implemented").length;
    const open = reqs.filter((req) => req.status === "open question" || req.status === "draft").length;
    const covered = reqs.filter((req) => req.covered).length;
    const coveragePct = total ? Math.round((covered / total) * 100) : 0;
    const readyPct = total ? Math.round((signedOff / total) * 100) : 0;

    const tickets = projectTickets(project);
    const statusOrder = (window.AC && window.AC.projectTicketStatuses ? window.AC.projectTicketStatuses(project) : window.AC && window.AC.PROJECT_TICKET_STATUSES) || ["Open", "In progress", "Partially done", "Ready for review", "Missing info", "Blocked", "Done"];
    const counts = Object.fromEntries(statusOrder.map((status) => [status, 0]));
    tickets.forEach((ticket) => { counts[ticket.status || (ticket.done ? "Done" : "Open")] = (counts[ticket.status || (ticket.done ? "Done" : "Open")] || 0) + 1; });
    const openTickets = tickets.filter((ticket) => (ticket.status || (ticket.done ? "Done" : "Open")) !== "Done").length;
    const openQuestionTickets = tickets.filter((ticket) => (ticket.status || "") === "Missing info").length;
    const blockedTickets = tickets.filter((ticket) => (ticket.status || "") === "Blocked").length;
    const blockedish = tickets.filter((ticket) => ["Blocked", "Missing info"].includes(ticket.status || "")).length;

    const activeMilestones = (project.milestones || []).filter((milestone) => !milestoneDone(milestone) && milestone.dueDate);
    const overdueMs = activeMilestones.filter((milestone) => daysBetween(milestone.dueDate, TODAY) > 0);
    const dueSoonMs = activeMilestones.filter((milestone) => daysBetween(TODAY, milestone.dueDate) >= 0 && daysBetween(TODAY, milestone.dueDate) <= 7);
    const nextMs = activeMilestones.slice().sort((a, b) => String(a.dueDate).localeCompare(String(b.dueDate)))[0] || null;

    let risk = "on-track";
    if (project.status === "On hold") risk = "hold";
    else if (project.status === "Completed") risk = "done";
    else if (overdueMs.length > 0 || blockedish >= 2) risk = "at-risk";
    else if (dueSoonMs.length > 0 && (blockedish > 0 || open > 0)) risk = "watch";

    return { total, signedOff, open, covered, coveragePct, readyPct, tickets, counts, openTickets, openQuestionTickets, blockedTickets, blockedish, overdueMs, dueSoonMs, nextMs, risk };
  }

  const WORK_PACKAGE_PHASES = ["Gathering/Intake", "Design/OKF Snapshot", "Specs", "Planning/Tickets", "Implementation", "Done"];

  function projectWorkPackagePhases(project) {
    return window.AC && window.AC.projectPhases ? window.AC.projectPhases(project) : WORK_PACKAGE_PHASES;
  }

  function workPackagePhaseIndex(phase, project) {
    const phases = projectWorkPackagePhases(project);
    const cleanPhase = window.AC && window.AC.cleanProjectPhase ? window.AC.cleanProjectPhase(phase, project) : phase;
    const clean = String(cleanPhase || "").trim().toLowerCase();
    const index = phases.findIndex((item) => item.toLowerCase() === clean);
    return index === -1 ? 0 : index;
  }

  function projectPhaseSummary(project) {
    const phases = projectWorkPackagePhases(project);
    const defaultWorkPackageName = window.AC && window.AC.projectDefaultWorkPackageName ? window.AC.projectDefaultWorkPackageName(project) : "Core app";
    const workPackages = (project.workPackages || [])
      .slice()
      .sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0) || String(a.name || "").localeCompare(String(b.name || "")))
      .map((workPackage) => {
        const phaseIndex = workPackagePhaseIndex(workPackage.phase, project);
        return { id: workPackage.id, name: workPackage.name || defaultWorkPackageName, phase: phases[phaseIndex], phaseIndex, phaseChangedAt: workPackage.phaseChangedAt || null };
      });
    const total = workPackages.length;
    const done = workPackages.filter((workPackage) => workPackage.phase === "Done").length;
    const maxProgress = total * (phases.length - 1);
    const pct = maxProgress ? Math.round((workPackages.reduce((sum, workPackage) => sum + workPackage.phaseIndex, 0) / maxProgress) * 100) : 0;
    const current = workPackages.slice().sort((a, b) => a.phaseIndex - b.phaseIndex)[0] || null;
    return { phases, workPackages, done, total, pct, current };
  }

  function upcomingMeetings(db, horizonDays) {
    const end = toIso(new Date(parseIso(TODAY).getTime() + (horizonDays * 86400000)));
    return (db.calendarEvents || [])
      .map((event) => window.CAL ? window.CAL.toMeeting(event) : event)
      .filter((meeting) => meeting.date >= TODAY && meeting.date <= end && meeting.status !== "Cancelled")
      .sort((a, b) => a.date.localeCompare(b.date) || String(a.start || "").localeCompare(String(b.start || "")));
  }

  function meetingsByDay(db, horizonDays) {
    const meetings = upcomingMeetings(db, horizonDays);
    const map = new Map();
    for (let i = 0; i <= horizonDays; i++) {
      const isoDate = toIso(new Date(parseIso(TODAY).getTime() + (i * 86400000)));
      const items = meetings.filter((meeting) => meeting.date === isoDate);
      if (items.length) map.set(isoDate, items);
    }
    return map;
  }

  function openMeetingFollowups(db) {
    return (db.calendarEvents || []).flatMap((event) => {
      const meeting = window.CAL ? window.CAL.toMeeting(event) : event;
      return (meeting.results || [])
        .filter((result) => ["Action", "Decision", "Risk", "Question"].includes(result.type))
        .map((result, index) => ({ ...result, key: `${meeting.id}:${result.id || index}`, meeting, event }));
    }).sort((a, b) => a.meeting.date.localeCompare(b.meeting.date));
  }

  window.MODEL = { TODAY, totals, enrich, monthlyPnL, receivables, ledger, cashflow, daysBetween, yearOf, monthIndex, expenseInMonth, expenseOccurrences, expensePaymentRows, employeeMonthlyWage, currentWagePeriod, wageExpenseAmount, wageExpenseRecurrence, isDraft, isCancelled, projectWorkPackages, projectTickets, projectKnowledgeConcepts, projectRequirements, projectReadiness, WORK_PACKAGE_PHASES, projectWorkPackagePhases, projectPhaseSummary, milestoneDone, upcomingMeetings, meetingsByDay, openMeetingFollowups };
})();
